import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, DatabaseBackup, Trash2 } from "lucide-react";

const BTN = "px-4 py-2 rounded-full border-[3px] border-[#374151] font-black text-sm shadow-[0_3px_0_0_#374151] active:translate-y-[3px] active:shadow-none";
const PILL = { done: "bg-[var(--tp-success)] text-white", failed: "bg-[#FF4B4B] text-white", running: "bg-[var(--tp-accent)]" };
const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export default function AdminBackups({ api }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/admin/backups").then((r) => setRows(r.data)), [api]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!rows.some((b) => b.status === "running")) return;
    const t = setTimeout(load, 2000);
    return () => clearTimeout(t);
  }, [rows, load]);

  const now = async () => { await api.post("/admin/backups"); toast.success("Backup started"); load(); };
  const download = async (b) => {
    try {
      const r = await api.get(`/admin/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = b.storage_path.split("/").pop();
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const hide = async (id) => { if (!window.confirm("Remove this backup from the list?")) return; await api.delete(`/admin/backups/${id}`); load(); };

  return (
    <div>
      <div className="sticker-card p-6 flex items-center gap-4 flex-wrap" style={{ background: "#E6F7FF" }}>
        <DatabaseBackup size={40} strokeWidth={2.5} />
        <div className="flex-1 min-w-[220px]">
          <p className="font-black text-lg">Automatic weekly backups</p>
          <p className="text-slate-600 text-sm">Every Sunday at 3:00 AM (UTC), a full copy of the database is saved to secure cloud storage. Download any copy here.</p>
        </div>
        <button onClick={now} className={`${BTN} bg-[var(--tp-primary)] text-white`} data-testid="backup-now-btn">Back up now</button>
      </div>
      <div className="sticker mt-6" data-testid="backups-list">
        {rows.length === 0 && <p className="p-5 text-slate-500">No backups yet. The first one runs this Sunday, or tap "Back up now".</p>}
        {rows.map((b) => (
          <div key={b.id} className="p-4 border-b-[2px] border-slate-200 last:border-0 flex items-center gap-3 flex-wrap" data-testid={`backup-row-${b.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-black">{new Date(b.created_at).toLocaleString()}</p>
              <p className="text-sm text-slate-500">
                {b.trigger === "weekly" ? "Weekly automatic" : "Manual"}
                {b.status === "done" && ` • ${fmtSize(b.size)} • ${Object.values(b.counts || {}).reduce((a, c) => a + c, 0)} records`}
                {b.status === "failed" && ` • ${b.error || "failed"}`}
              </p>
            </div>
            <span className={`px-3 py-1 rounded-full border-[2px] border-[#374151] font-black text-xs uppercase ${PILL[b.status]}`}>{b.status}</span>
            {b.status === "done" && (
              <button onClick={() => download(b)} className={`${BTN} bg-white inline-flex items-center gap-1`} data-testid={`backup-download-${b.id}`}>
                <Download size={14} strokeWidth={2.5} /> Download
              </button>
            )}
            <button onClick={() => hide(b.id)} className="p-2 text-[#FF4B4B]" data-testid={`backup-hide-${b.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
